/**
 * WeaveIn.ai Platform - Member Enrollments Tab
 * Displays and manages member plan enrollments within member profile
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard,
  Plus,
  Calendar,
  Users,
  Building,
  Shield,
  AlertCircle,
  Check,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  healthPlansAPI,
  MemberEnrollment,
  MemberEnrollmentCreate,
  COVERAGE_TIERS,
  ENROLLMENT_SOURCES,
  RELATIONSHIP_TYPES,
  PRODUCT_TYPES,
} from '@/services/api/healthPlans';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// ============================================================================
// Types
// ============================================================================

interface MemberEnrollmentsTabProps {
  memberId: number;
  memberName?: string;
  isReadOnly?: boolean;
}

// ============================================================================
// Enrollment Card
// ============================================================================

interface EnrollmentCardProps {
  enrollment: MemberEnrollment;
  onTerminate: (enrollmentId: number, date: string) => void;
  isReadOnly: boolean;
}

const EnrollmentCard: React.FC<EnrollmentCardProps> = ({ enrollment, onTerminate, isReadOnly }) => {
  const { fmt } = useFormattedDate();
  const [showTerminate, setShowTerminate] = useState(false);
  const [terminationDate, setTerminationDate] = useState(new Date().toISOString().split('T')[0]);

  const formatDate = (date?: string) => date ? fmt.short(date) : '-';
  const formatCurrency = (amount?: number) => amount ? `$${amount.toLocaleString()}` : '-';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'TERMINATED': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'COBRA': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getCOBLabel = (order: number) => {
    switch (order) {
      case 1: return 'Primary';
      case 2: return 'Secondary';
      case 3: return 'Tertiary';
      default: return `COB #${order}`;
    }
  };

  const handleTerminate = () => {
    onTerminate(enrollment.id, terminationDate);
    setShowTerminate(false);
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-3">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
              {enrollment.health_plan_name || enrollment.employer_plan_name || 'Unknown Plan'}
              {enrollment.cob_order && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                  {getCOBLabel(enrollment.cob_order)}
                </span>
              )}
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {enrollment.product_name && `${enrollment.product_name} · `}
              {enrollment.product_type && PRODUCT_TYPES.find(p => p.value === enrollment.product_type)?.label}
            </p>
          </div>
          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(enrollment.status)}`}>
            {enrollment.status}
          </span>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-xs">Member ID</p>
            <p className="font-medium text-gray-900 dark:text-white">{enrollment.member_id_number || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-xs">Group Number</p>
            <p className="font-medium text-gray-900 dark:text-white">{enrollment.group_number || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-xs">Subscriber ID</p>
            <p className="font-medium text-gray-900 dark:text-white">{enrollment.subscriber_id || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-xs">Relationship</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {RELATIONSHIP_TYPES.find(r => r.value === enrollment.relationship_to_subscriber)?.label || enrollment.relationship_to_subscriber}
            </p>
          </div>
        </div>

        {/* Dates & Coverage */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Effective Date</p>
              <p className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(enrollment.effective_date)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Termination Date</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {enrollment.termination_date ? formatDate(enrollment.termination_date) : 'Active'}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Coverage Tier</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {COVERAGE_TIERS.find(c => c.value === enrollment.coverage_tier)?.label || enrollment.coverage_tier || '-'}
              </p>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-xs">Enrollment Source</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {ENROLLMENT_SOURCES.find(s => s.value === enrollment.enrollment_source)?.label || enrollment.enrollment_source || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Premiums */}
        {(enrollment.employee_premium_amount || enrollment.employer_premium_amount) && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-6 text-sm">
              {enrollment.employee_premium_amount && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">Employee Premium</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(enrollment.employee_premium_amount)}/mo</p>
                </div>
              )}
              {enrollment.employer_premium_amount && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">Employer Premium</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(enrollment.employer_premium_amount)}/mo</p>
                </div>
              )}
              {enrollment.cobra_premium_amount && (
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">COBRA Premium</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(enrollment.cobra_premium_amount)}/mo</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Terminate Form */}
        {showTerminate && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300 mb-2">Terminate this enrollment?</p>
              <div className="flex gap-3 items-center">
                <input
                  type="date"
                  value={terminationDate}
                  onChange={(e) => setTerminationDate(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <button
                  onClick={handleTerminate}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                >
                  Confirm Termination
                </button>
                <button
                  onClick={() => setShowTerminate(false)}
                  className="px-3 py-1.5 text-gray-600 dark:text-gray-300 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      {!isReadOnly && enrollment.status === 'ACTIVE' && !showTerminate && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowTerminate(true)}
            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
          >
            Terminate Enrollment
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Enrollment Form
// ============================================================================

interface EnrollmentFormProps {
  memberId: number;
  onSave: () => void;
  onCancel: () => void;
}

const EnrollmentForm: React.FC<EnrollmentFormProps> = ({ memberId, onSave, onCancel }) => {
  const [formData, setFormData] = useState<MemberEnrollmentCreate>({
    enrollment_date: new Date().toISOString().split('T')[0],
    effective_date: new Date().toISOString().split('T')[0],
    relationship_to_subscriber: 'SELF',
  });
  const [enrollmentType, setEnrollmentType] = useState<'health_plan' | 'employer'>('employer');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (enrollmentType === 'health_plan' && !formData.health_plan_product_id) {
      setError('Please select a health plan product');
      return;
    }
    if (enrollmentType === 'employer' && !formData.employer_plan_id) {
      setError('Please select an employer plan');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await healthPlansAPI.createMemberEnrollment(memberId, formData);
      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to create enrollment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4">New Enrollment</h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Enrollment Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Enrollment Type</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="enrollmentType"
                value="employer"
                checked={enrollmentType === 'employer'}
                onChange={() => {
                  setEnrollmentType('employer');
                  setFormData({ ...formData, health_plan_product_id: undefined });
                }}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Employer Plan</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="enrollmentType"
                value="health_plan"
                checked={enrollmentType === 'health_plan'}
                onChange={() => {
                  setEnrollmentType('health_plan');
                  setFormData({ ...formData, employer_plan_id: undefined });
                }}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Health Plan Product</span>
            </label>
          </div>
        </div>

        {/* Plan Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {enrollmentType === 'employer' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employer Plan ID *</label>
              <input
                type="number"
                value={formData.employer_plan_id || ''}
                onChange={(e) => setFormData({ ...formData, employer_plan_id: parseInt(e.target.value) || undefined })}
                placeholder="Enter employer plan ID"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Health Plan Product ID *</label>
              <input
                type="number"
                value={formData.health_plan_product_id || ''}
                onChange={(e) => setFormData({ ...formData, health_plan_product_id: parseInt(e.target.value) || undefined })}
                placeholder="Enter product ID"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Enrollment Source</label>
            <select
              value={formData.enrollment_source || ''}
              onChange={(e) => setFormData({ ...formData, enrollment_source: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">Select...</option>
              {ENROLLMENT_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {/* Member Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Member ID Number</label>
            <input
              type="text"
              value={formData.member_id_number || ''}
              onChange={(e) => setFormData({ ...formData, member_id_number: e.target.value })}
              placeholder="Plan-assigned member ID"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Group Number</label>
            <input
              type="text"
              value={formData.group_number || ''}
              onChange={(e) => setFormData({ ...formData, group_number: e.target.value })}
              placeholder="Group number"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subscriber ID</label>
            <input
              type="text"
              value={formData.subscriber_id || ''}
              onChange={(e) => setFormData({ ...formData, subscriber_id: e.target.value })}
              placeholder="Subscriber ID"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>

        {/* Relationship & Coverage */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Relationship to Subscriber</label>
            <select
              value={formData.relationship_to_subscriber}
              onChange={(e) => setFormData({ ...formData, relationship_to_subscriber: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              {RELATIONSHIP_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coverage Tier</label>
            <select
              value={formData.coverage_tier || ''}
              onChange={(e) => setFormData({ ...formData, coverage_tier: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="">Select...</option>
              {COVERAGE_TIERS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Enrollment Date *</label>
            <input
              type="date"
              value={formData.enrollment_date}
              onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective Date *</label>
            <input
              type="date"
              value={formData.effective_date}
              onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Termination Date</label>
            <input
              type="date"
              value={formData.termination_date || ''}
              onChange={(e) => setFormData({ ...formData, termination_date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>

        {/* Premiums */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employee Premium (Monthly)</label>
            <input
              type="number"
              value={formData.employee_premium_amount || ''}
              onChange={(e) => setFormData({ ...formData, employee_premium_amount: parseFloat(e.target.value) || undefined })}
              placeholder="$0.00"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Employer Premium (Monthly)</label>
            <input
              type="number"
              value={formData.employer_premium_amount || ''}
              onChange={(e) => setFormData({ ...formData, employer_premium_amount: parseFloat(e.target.value) || undefined })}
              placeholder="$0.00"
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Creating...' : 'Create Enrollment'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export function MemberEnrollmentsTab({ memberId, memberName, isReadOnly = false }: MemberEnrollmentsTabProps) {
  const [enrollments, setEnrollments] = useState<MemberEnrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'ALL'>('ACTIVE');

  // Load enrollments
  const loadEnrollments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const status = statusFilter === 'ACTIVE' ? 'ACTIVE' : undefined;
      const data = await healthPlansAPI.listMemberEnrollments(memberId, status);
      setEnrollments(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  }, [memberId, statusFilter]);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  // Handle terminate
  const handleTerminate = async (enrollmentId: number, terminationDate: string) => {
    try {
      await healthPlansAPI.terminateEnrollment(memberId, enrollmentId, terminationDate);
      loadEnrollments();
    } catch (err: any) {
      setError(err.message || 'Failed to terminate enrollment');
    }
  };

  // Handle form save
  const handleFormSave = () => {
    setShowForm(false);
    loadEnrollments();
  };

  // Group by COB order
  const activeEnrollments = enrollments.filter(e => e.status === 'ACTIVE').sort((a, b) => a.cob_order - b.cob_order);
  const inactiveEnrollments = enrollments.filter(e => e.status !== 'ACTIVE');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Plan Enrollments
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {activeEnrollments.length} active enrollment{activeEnrollments.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'ACTIVE' | 'ALL')}
            className="px-3 py-1.5 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            <option value="ACTIVE">Active Only</option>
            <option value="ALL">All Enrollments</option>
          </select>
          {!isReadOnly && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Add Enrollment
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto underline text-sm">Dismiss</button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <EnrollmentForm
          memberId={memberId}
          onSave={handleFormSave}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : enrollments.length === 0 ? (
        /* Empty State */
        <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No enrollments</h4>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {statusFilter === 'ACTIVE' ? 'No active plan enrollments' : 'No plan enrollments found'}
          </p>
          {!isReadOnly && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Add Enrollment
            </button>
          )}
        </div>
      ) : (
        /* Enrollments List */
        <div className="space-y-4">
          {activeEnrollments.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Active Coverage</h4>
              <div className="space-y-3">
                {activeEnrollments.map(enrollment => (
                  <EnrollmentCard
                    key={enrollment.id}
                    enrollment={enrollment}
                    onTerminate={handleTerminate}
                    isReadOnly={isReadOnly}
                  />
                ))}
              </div>
            </div>
          )}

          {statusFilter === 'ALL' && inactiveEnrollments.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 mt-6">
                Previous Enrollments ({inactiveEnrollments.length})
              </h4>
              <div className="space-y-3 opacity-75">
                {inactiveEnrollments.map(enrollment => (
                  <EnrollmentCard
                    key={enrollment.id}
                    enrollment={enrollment}
                    onTerminate={handleTerminate}
                    isReadOnly={true}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
